import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { serverLink } from "../../../resources/url";
import DataLoader from "../../common/dataLoader/dataLoader";
import TransactionDataTable from "./transaction-data-table";

const C = {
    primary: "#206bc4",
    success: "#2fb344",
    warning: "#f76707",
    danger: "#d63939",
    info: "#4299e1",
    purple: "#ae3ec9",
};

const TABLE_HEADERS = [
    "S/N",
    "Date Paid",
    "Transaction ID",
    "Student ID",
    "Student Name",
    "Email",
    "Payment Type",
    "Amount (₦)",
    "Reference",
    "Session",
    "Level",
    "Semester",
];

const parseDate = (dp) => {
    if (!dp || !String(dp).trim()) return null;
    const s = String(dp).trim();

    const mysql = s.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/
    );
    if (mysql) {
        const d = new Date(
            +mysql[1],
            +mysql[2] - 1,
            +mysql[3],
            +(mysql[4] || 0),
            +(mysql[5] || 0),
            +(mysql[6] || 0)
        );
        if (!isNaN(d.getTime())) return d;
    }

    const d = new Date(s);
    if (!isNaN(d.getTime())) return d;

    const pts = s.split("/");
    if (pts.length === 3) {
        const d2 = new Date(
            `${pts[2]}-${pts[1].padStart(2, "0")}-${pts[0].padStart(2, "0")}`
        );
        if (!isNaN(d2.getTime())) return d2;
    }
    return null;
};

const toDayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const parseFilterDate = (value) => {
    if (!value) return null;
    const [y, m, day] = value.split("-").map(Number);
    if (!y || !m || !day) return null;
    return new Date(y, m - 1, day);
};

const parseYear = (dp, fallbackYear) => {
    const d = parseDate(dp);
    if (d) return d.getFullYear();
    if (fallbackYear != null && String(fallbackYear).trim() !== "") {
        const yr = parseInt(String(fallbackYear), 10);
        return isNaN(yr) ? null : yr;
    }
    return null;
};

const formatParsedDate = (d) => {
    if (!d) return "—";
    return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const fmtCurrency = (n) =>
    `₦${Number(n).toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const fmtShort = (n) => {
    if (n >= 1_000_000_000) return `₦${(n / 1e9).toFixed(2)}B`;
    if (n >= 1_000_000) return `₦${(n / 1e6).toFixed(2)}M`;
    if (n >= 1_000) return `₦${(n / 1e3).toFixed(1)}K`;
    return `₦${Number(n).toFixed(2)}`;
};

const parseAmount = (val) => {
    const n = parseFloat(String(val).replace(/[^0-9.]/g, ""));
    return isNaN(n) ? 0 : n;
};

const enrichPayment = (p) => {
    const parsedDate = parseDate(p.DATE_PAID);
    const year = parsedDate
        ? parsedDate.getFullYear()
        : parseYear(p.DATE_PAID, p.YEAR);
    return {
        ...p,
        _parsedDate: parsedDate,
        _year: year,
        _payType: (p.PAYMENT_TYPE || "").trim(),
        _amount: parseAmount(p.AMOUNT),
        _payerKey: p.STUDENTID || p.APPID || p.EMAIL || "",
    };
};

const filterLabelStyle = {
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: ".05em",
    color: "#64748b",
};

/** Debounce a setter so rapid calls only fire one re-render after `delay` ms. */
function useDebouncedSetter(setter, delay = 120) {
    const timer = useRef(null);
    return useCallback(
        (value) => {
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setter(value), delay);
        },
        [setter, delay]
    );
}

function TransactionListReport(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [payments, setPayments] = useState([]);
    const [filterYear, setFilterYear] = useState("All");
    const [filterPayType, setFilterPayType] = useState("All");
    const [filterDateFrom, setFilterDateFrom] = useState("");
    const [filterDateTo, setFilterDateTo] = useState("");

    // Debounced setters — filter changes batch into one DataTables re-render
    const setFilterYearDebounced = useDebouncedSetter(setFilterYear);
    const setFilterPayTypeDebounced = useDebouncedSetter(setFilterPayType);
    const setFilterDateFromDebounced = useDebouncedSetter(setFilterDateFrom);
    const setFilterDateToDebounced = useDebouncedSetter(setFilterDateTo);

    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        try {
            const result = await axios.get(`${serverLink}staff/report/payment/analytics`);
            const raw = result.data || [];
            setPayments(raw.map(enrichPayment));
        } catch (e) {
            console.log("NETWORK ERROR");
        } finally {
            setIsLoading(false);
        }
    };

    const allYears = useMemo(() => {
        const s = new Set(payments.map((p) => p._year).filter(Boolean));
        return [...s].sort((a, b) => a - b).map(String);
    }, [payments]);

    const allPayTypes = useMemo(() => {
        const s = new Set(payments.map((p) => p._payType).filter(Boolean));
        return [...s].sort();
    }, [payments]);

    const filtered = useMemo(() => {
        const from = parseFilterDate(filterDateFrom);
        const to = parseFilterDate(filterDateTo);
        const hasDateFilter = Boolean(from || to);

        return payments.filter((p) => {
            const yrStr = p._year != null ? String(p._year) : null;

            if (filterYear !== "All" && yrStr !== filterYear) return false;
            if (filterPayType !== "All" && p._payType !== filterPayType) return false;

            if (hasDateFilter) {
                if (!p._parsedDate) return false;
                const day = toDayStart(p._parsedDate);
                if (from && day < from) return false;
                if (to && day > to) return false;
            }

            return true;
        });
    }, [payments, filterYear, filterPayType, filterDateFrom, filterDateTo]);

    const hasFilter =
        filterYear !== "All" || filterPayType !== "All" || filterDateFrom || filterDateTo;

    const totalTxns = filtered.length;
    const totalAmount = useMemo(
        () => filtered.reduce((s, p) => s + p._amount, 0),
        [filtered]
    );
    const avgAmount = totalTxns > 0 ? totalAmount / totalTxns : 0;
    const uniquePayers = useMemo(
        () => new Set(filtered.map((p) => p._payerKey).filter(Boolean)).size,
        [filtered]
    );

    const tableRows = useMemo(
        () =>
            filtered.map((item, index) => [
                index + 1,
                formatParsedDate(item._parsedDate),
                item.TRANSACTION_ID || "—",
                item.STUDENTID || item.APPID || "—",
                item.STUDENT_NAME || "—",
                item.EMAIL || "—",
                item.PAYMENT_TYPE || "—",
                Number(item.AMOUNT || 0).toLocaleString("en-NG", {
                    minimumFractionDigits: 2,
                }),
                item.REFERENCE || "—",
                item.SESSION || item.SESSION_CODE || "—",
                item.LEVEL || "—",
                item.SEMESTER || "—",
            ]),
        [filtered]
    );

    const clearFilters = () => {
        setFilterYear("All");
        setFilterPayType("All");
        setFilterDateFrom("");
        setFilterDateTo("");
    };

    if (isLoading) {
        return (
            <div className="container-xl" style={{ height: "700px" }}>
                <div className="page-header d-print-none">
                    <DataLoader />
                </div>
            </div>
        );
    }

    const handlePrint = () => window.print();

    return (
        <>
            <style>{`
                @media print {
                    /* Hide browser chrome, nav, filters, action buttons */
                    .navbar, .navbar-vertical, .sidebar, .page-header,
                    .print-hide, .btn, select, input[type="date"] { display: none !important; }

                    /* Always show the stats cards and table on print */
                    .page-body { padding: 0 !important; }

                    /* Print title */
                    .print-title {
                        display: block !important;
                        text-align: center;
                        font-size: 1.3rem;
                        font-weight: 700;
                        margin-bottom: 12px;
                    }
                }
                @media screen {
                    .print-title { display: none; }
                }
            `}</style>

            <div className="container-xl">
                <div className="page-header d-print-none">
                    <div className="row align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Finance / Reports</div>
                            <h2 className="page-title">Transaction List Report</h2>
                        </div>
                        <div className="col-auto ms-auto">
                            <button
                                className="btn btn-primary d-print-none"
                                onClick={handlePrint}
                                style={{ display: "flex", alignItems: "center", gap: "6px" }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2"
                                    stroke="currentColor"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
                                    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
                                    <rect x="7" y="13" width="10" height="8" rx="2" />
                                </svg>
                                Print Report
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Visible only during printing */}
            <div className="container-xl print-title">
                Transaction List Report
                {hasFilter && (
                    <div style={{ fontSize: "0.85rem", fontWeight: 400, color: "#555", marginTop: 4 }}>
                        {filterYear !== "All" && <span>Year: {filterYear} &nbsp;</span>}
                        {filterPayType !== "All" && <span>Type: {filterPayType} &nbsp;</span>}
                        {filterDateFrom && <span>From: {filterDateFrom} &nbsp;</span>}
                        {filterDateTo && <span>To: {filterDateTo}</span>}
                    </div>
                )}
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <div className="row row-deck row-cards mb-3">
                        {[
                            {
                                label: "Total Transactions",
                                val: totalTxns.toLocaleString(),
                                sub: null,
                                accent: C.primary,
                            },
                            {
                                label: "Total Amount",
                                val: fmtShort(totalAmount),
                                sub: fmtCurrency(totalAmount),
                                accent: C.success,
                            },
                            {
                                label: "Avg. Per Transaction",
                                val: fmtShort(avgAmount),
                                sub: fmtCurrency(avgAmount),
                                accent: C.info,
                            },
                            {
                                label: "Unique Payers",
                                val: uniquePayers.toLocaleString(),
                                sub: null,
                                accent: C.purple,
                            },
                        ].map(({ label, val, sub, accent }) => (
                            <div key={label} className="col-6 col-md-6 col-lg-3">
                                <div className="card" style={{ borderTop: `3px solid ${accent}` }}>
                                    <div className="card-body p-3">
                                        <div style={{ ...filterLabelStyle, marginBottom: 6 }}>
                                            {label}
                                        </div>
                                        <div
                                            className="fw-bold"
                                            style={{
                                                fontSize: "clamp(1.1rem, 4vw, 1.45rem)",
                                                wordBreak: "break-word",
                                                color: accent,
                                            }}
                                        >
                                            {val}
                                        </div>
                                        {sub && (
                                            <div
                                                className="text-muted mt-1"
                                                style={{ fontSize: "0.7rem" }}
                                            >
                                                {sub}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div
                        className="card mb-3"
                        style={{ border: "1.5px solid #d0e2f7", background: "#f8fbff" }}
                    >
                        <div className="card-body py-3">
                            <div className="row g-3 align-items-end">
                                <div className="col-12 col-sm-6 col-lg-auto">
                                    <label className="form-label mb-1" style={filterLabelStyle}>
                                        Year
                                    </label>
                                    <select
                                        className="form-select form-select-sm w-100"
                                        value={filterYear}
                                        onChange={(e) => setFilterYearDebounced(e.target.value)}
                                        style={{ minWidth: 0 }}
                                    >
                                        <option value="All">All Years</option>
                                        {allYears.map((y) => (
                                            <option key={y} value={y}>
                                                {y}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-12 col-sm-6 col-lg-auto">
                                    <label className="form-label mb-1" style={filterLabelStyle}>
                                        Payment Type
                                    </label>
                                    <select
                                        className="form-select form-select-sm w-100"
                                        value={filterPayType}
                                        onChange={(e) => setFilterPayTypeDebounced(e.target.value)}
                                        style={{ minWidth: 0 }}
                                    >
                                        <option value="All">All Types</option>
                                        {allPayTypes.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-12 col-sm-6 col-lg-auto">
                                    <label className="form-label mb-1" style={filterLabelStyle}>
                                        Date From
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm w-100"
                                        value={filterDateFrom}
                                        max={filterDateTo || undefined}
                                        onChange={(e) => setFilterDateFromDebounced(e.target.value)}
                                    />
                                </div>

                                <div className="col-12 col-sm-6 col-lg-auto">
                                    <label className="form-label mb-1" style={filterLabelStyle}>
                                        Date To
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm w-100"
                                        value={filterDateTo}
                                        min={filterDateFrom || undefined}
                                        onChange={(e) => setFilterDateToDebounced(e.target.value)}
                                    />
                                </div>

                                {hasFilter && (
                                    <div className="col-12 col-sm-6 col-lg-auto">
                                        <button
                                            className="btn btn-sm btn-outline-danger w-100 w-lg-auto"
                                            onClick={clearFilters}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                strokeWidth="2"
                                                stroke="currentColor"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="me-1"
                                            >
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <line x1="18" y1="6" x2="6" y2="18" />
                                                <line x1="6" y1="6" x2="18" y2="18" />
                                            </svg>
                                            Clear Filters
                                        </button>
                                    </div>
                                )}

                                <div className="col-12 col-lg text-lg-end text-center">
                                    <span className="text-muted" style={{ fontSize: "0.76rem" }}>
                                        Showing{" "}
                                        <strong>{totalTxns.toLocaleString()}</strong> of{" "}
                                        <strong>{payments.length.toLocaleString()}</strong>{" "}
                                        transactions
                                        {hasFilter && (
                                            <span
                                                className="ms-2 badge"
                                                style={{
                                                    background: "#206bc418",
                                                    color: "#206bc4",
                                                    fontSize: "10px",
                                                }}
                                            >
                                                Filtered
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body">
                            <h3 className="card-title">Transaction List</h3>
                            {/* Always keep TransactionDataTable mounted to prevent React/DataTables DOM conflict.
                                Unmounting while DataTables owns the <table> node causes a removeChild crash.
                                The empty-state message is shown as an overlay instead. */}
                            {filtered.length === 0 && (
                                <div className="text-center text-muted py-4">
                                    No transactions match the selected filters.
                                    {hasFilter && (
                                        <div className="mt-2">
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={clearFilters}
                                            >
                                                Clear filters
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div
                                className="table-responsive"
                                style={{
                                    overflowX: "auto",
                                    WebkitOverflowScrolling: "touch",
                                    display: filtered.length === 0 ? "none" : undefined,
                                }}
                            >
                                <TransactionDataTable
                                    headers={TABLE_HEADERS}
                                    rows={tableRows}
                                    title="Transaction List Report"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

const mapStateToProps = (state) => ({
    loginData: state.loginDetails,
});

export default connect(mapStateToProps, null)(TransactionListReport);
